import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Popover,
  Typography,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DropdownIcon from '../icons/DropdownIcon';

const TriggerButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '8px 16px',
  gap: '8px',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  minWidth: 180,
  justifyContent: 'space-between',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.4)',
  }
}));

const PopoverContent = styled(Box)(({ theme }) => ({
  padding: '16px',
  minWidth: 340,
  maxWidth: 420,
  fontFamily: 'Inter, sans-serif'
}));

const SectionTitle = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '8px'
});

const PresetChip = styled(Chip)(({ selected }) => ({
  borderRadius: '8px',
  fontWeight: 500,
  fontSize: '13px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: selected ? '#0B91D6' : '#F3F4F6',
  color: selected ? 'white' : '#374151',
  border: selected ? 'none' : '1px solid #E5E7EB',
  padding: '4px 8px',
  '&:hover': {
    backgroundColor: selected ? '#0980c2' : '#E5E7EB',
    transform: 'translateY(-1px)'
  }
}));

const DayButton = styled(Button)(({ selected, inRange, isToday, disabled }) => ({
  minWidth: '40px',
  width: '40px',
  height: '40px',
  padding: 0,
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: selected ? 600 : isToday ? 600 : 400,
  textTransform: 'none',
  backgroundColor: selected ? '#0B91D6' : inRange ? 'rgba(11, 145, 214, 0.15)' : 'transparent',
  color: disabled ? '#D1D5DB' : selected ? 'white' : inRange ? '#0B91D6' : isToday ? '#0B91D6' : '#374151',
  border: isToday && !selected ? '2px solid #0B91D6' : '1px solid transparent',
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: disabled ? 'transparent' : selected ? '#0980c2' : 'rgba(11, 145, 214, 0.1)',
  },
  '&.Mui-disabled': {
    color: '#D1D5DB'
  }
}));

const MonthButton = styled(Button)(({ selected, inRange }) => ({
  minWidth: '75px',
  padding: '10px 14px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: selected ? 600 : 500,
  textTransform: 'none',
  backgroundColor: selected ? '#0B91D6' : inRange ? 'rgba(11, 145, 214, 0.1)' : 'transparent',
  color: selected ? 'white' : inRange ? '#0B91D6' : '#374151',
  border: selected ? 'none' : '1px solid transparent',
  '&:hover': {
    backgroundColor: selected ? '#0980c2' : 'rgba(11, 145, 214, 0.15)',
    border: '1px solid rgba(11, 145, 214, 0.3)'
  }
}));

const YearButton = styled(Button)(({ selected, inRange }) => ({
  minWidth: '70px',
  padding: '12px 16px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: selected ? 700 : 500,
  textTransform: 'none',
  backgroundColor: selected ? '#0B91D6' : inRange ? 'rgba(11, 145, 214, 0.15)' : 'transparent',
  color: selected ? 'white' : inRange ? '#0B91D6' : '#374151',
  border: selected ? 'none' : '1px solid #E5E7EB',
  '&:hover': {
    backgroundColor: selected ? '#0980c2' : 'rgba(11, 145, 214, 0.1)',
  }
}));

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DateRangePicker = ({
  selectedTimeRange = 'Month',
  onTimeRangeChange,
  onCustomDateChange,
  onCompareChange,
  compareEnabled = false,
  comparePeriod = null
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState('month'); // 'week' | 'month' | 'year'
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [endMonth, setEndMonth] = useState(null); // For month range selection
  const [selectedYear, setSelectedYear] = useState(null);
  const [endYear, setEndYear] = useState(null); // For year range selection
  
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Tab selection
  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectedMonth(null);
    setEndMonth(null);
    setSelectedYear(null);
    setEndYear(null);
  };

  // Week/Day selection
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    // Empty cells for days before the 1st
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    if (selectedStartDate && date.toDateString() === selectedStartDate.toDateString()) return true;
    if (selectedEndDate && date.toDateString() === selectedEndDate.toDateString()) return true;
    return false;
  };

  const isDateInRange = (date) => {
    if (!date || !selectedStartDate || !selectedEndDate) return false;
    return date > selectedStartDate && date < selectedEndDate;
  };

  const isDateToday = (date) => {
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    return date > today;
  };

  const handleDayClick = (date) => {
    if (!date || isDateDisabled(date)) return;
    
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // First selection or reset
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else {
      // Second selection
      if (date < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
      } else {
        setSelectedEndDate(date);
      }
    }
  };

  const handleApplyDateRange = () => {
    if (selectedStartDate) {
      const endDate = selectedEndDate || selectedStartDate;
      const label = selectedEndDate 
        ? `${selectedStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        : selectedStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      
      // Only call onCustomDateChange - it will handle both the date range and the label
      onCustomDateChange?.({
        startDate: selectedStartDate,
        endDate: endDate,
        label
      });
      handleClose();
    }
  };

  // Month selection - supports range selection
  const handleMonthSelect = (monthIndex) => {
    const year = viewDate.getFullYear();
    const clickedMonth = { year, month: monthIndex };
    
    if (!selectedMonth || (selectedMonth && endMonth)) {
      // First selection or reset after range
      setSelectedMonth(clickedMonth);
      setEndMonth(null);
    } else {
      // Second selection - create range
      const startM = selectedMonth;
      const endM = clickedMonth;
      
      // Ensure start is before end
      const start1 = startM.year * 12 + startM.month;
      const end1 = endM.year * 12 + endM.month;
      
      let finalStart, finalEnd;
      if (start1 <= end1) {
        finalStart = startM;
        finalEnd = endM;
      } else {
        finalStart = endM;
        finalEnd = startM;
      }
      
      setSelectedMonth(finalStart);
      setEndMonth(finalEnd);
      
      // Create date range from start of first month to end of last month
      const startDate = new Date(finalStart.year, finalStart.month, 1);
      const endDate = new Date(finalEnd.year, finalEnd.month + 1, 0);
      
      const label = finalStart.year === finalEnd.year && finalStart.month === finalEnd.month
        ? `${FULL_MONTHS[finalStart.month]} ${finalStart.year}`
        : `${MONTHS[finalStart.month]} ${finalStart.year} - ${MONTHS[finalEnd.month]} ${finalEnd.year}`;
      
      onCustomDateChange?.({ startDate, endDate, label });
      handleClose();
    }
  };

  const isMonthSelected = (monthIndex) => {
    const year = viewDate.getFullYear();
    return (selectedMonth?.year === year && selectedMonth?.month === monthIndex) ||
           (endMonth?.year === year && endMonth?.month === monthIndex);
  };

  const isMonthInRange = (monthIndex) => {
    if (!selectedMonth || !endMonth) return false;
    const year = viewDate.getFullYear();
    const m1 = selectedMonth.year * 12 + selectedMonth.month;
    const m2 = endMonth.year * 12 + endMonth.month;
    const current = year * 12 + monthIndex;
    return current > Math.min(m1, m2) && current < Math.max(m1, m2);
  };

  const isMonthDisabled = (monthIndex) => {
    const year = viewDate.getFullYear();
    return year > currentYear || (year === currentYear && monthIndex > currentMonth);
  };

  // Year selection - supports range selection
  const handleYearSelect = (year) => {
    if (!selectedYear || (selectedYear && endYear)) {
      // First selection or reset after range
      setSelectedYear(year);
      setEndYear(null);
    } else {
      // Second selection - create range
      const startY = Math.min(selectedYear, year);
      const endY = Math.max(selectedYear, year);
      
      setSelectedYear(startY);
      setEndYear(endY);
      
      const startDate = new Date(startY, 0, 1);
      const endDate = new Date(endY, 11, 31);
      
      const label = startY === endY ? `${startY}` : `${startY} - ${endY}`;
      
      onCustomDateChange?.({ startDate, endDate, label });
      handleClose();
    }
  };

  const isYearSelected = (year) => {
    return year === selectedYear || year === endYear;
  };

  const isYearInRange = (year) => {
    if (!selectedYear || !endYear) return false;
    return year > Math.min(selectedYear, endYear) && year < Math.max(selectedYear, endYear);
  };

  const generateYears = () => {
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      years.push(y);
    }
    return years;
  };

  // Display label
  const displayLabel = useMemo(() => {
    if (selectedMonth && endMonth) {
      return `${MONTHS[selectedMonth.month]} ${selectedMonth.year} - ${MONTHS[endMonth.month]} ${endMonth.year}`;
    }
    if (selectedYear && endYear) {
      return `${selectedYear} - ${endYear}`;
    }
    return selectedTimeRange;
  }, [selectedTimeRange, selectedMonth, endMonth, selectedYear, endYear]);

  const open = Boolean(anchorEl);
  
  return (
    <>
      <TriggerButton onClick={handleOpen}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon sx={{ fontSize: 16 }} />
          <span>{displayLabel}</span>
        </Box>
        <DropdownIcon width={7} height={4} color="white" />
      </TriggerButton>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            mt: 1
          }
        }}
      >
        <PopoverContent>
          {/* Tab Selection */}
          <SectionTitle>Select Period Type</SectionTitle>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <PresetChip
              label="Week"
              selected={activeTab === 'week'}
              onClick={() => handleTabSelect('week')}
            />
            <PresetChip
              label="Month"
              selected={activeTab === 'month'}
              onClick={() => handleTabSelect('month')}
            />
            <PresetChip
              label="Year"
              selected={activeTab === 'year'}
              onClick={() => handleTabSelect('year')}
            />
          </Box>
          
          <Divider sx={{ my: 2 }} />

          {/* Week/Day View */}
          {activeTab === 'week' && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <SectionTitle sx={{ mb: 0 }}>Select Date(s)</SectionTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', minWidth: 120, textAlign: 'center' }}>
                    {FULL_MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                    disabled={viewDate.getFullYear() === currentYear && viewDate.getMonth() >= currentMonth}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Day headers */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                {DAYS_OF_WEEK.map(day => (
                  <Typography key={day} sx={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#6B7280' }}>
                    {day}
                  </Typography>
                ))}
              </Box>

              {/* Calendar grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 2 }}>
                {generateCalendarDays().map((date, index) => (
                  <DayButton
                    key={index}
                    selected={isDateSelected(date)}
                    inRange={isDateInRange(date)}
                    isToday={isDateToday(date)}
                    disabled={isDateDisabled(date)}
                    onClick={() => handleDayClick(date)}
                  >
                    {date ? date.getDate() : ''}
                  </DayButton>
                ))}
              </Box>

              {/* Selection info */}
              {selectedStartDate && (
                <Box sx={{ p: 2, backgroundColor: '#F0F9FF', borderRadius: '8px', mb: 2 }}>
                  <Typography sx={{ fontSize: '12px', color: '#0369A1' }}>
                    {selectedEndDate 
                      ? `📅 ${selectedStartDate.toLocaleDateString()} → ${selectedEndDate.toLocaleDateString()}`
                      : `📅 ${selectedStartDate.toLocaleDateString()} (click another date to select range)`
                    }
                  </Typography>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyDateRange}
                disabled={!selectedStartDate}
                sx={{ 
                  borderRadius: '8px', 
                  textTransform: 'none',
                  backgroundColor: '#0B91D6',
                  '&:hover': { backgroundColor: '#0980c2' }
                }}
              >
                Apply {selectedEndDate ? 'Date Range' : 'Date'}
              </Button>
            </>
          )}

          {/* Month View */}
          {activeTab === 'month' && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <SectionTitle sx={{ mb: 0 }}>Select Month</SectionTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setViewDate(new Date(viewDate.getFullYear() - 1, 0, 1))}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', minWidth: 50, textAlign: 'center' }}>
                    {viewDate.getFullYear()}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setViewDate(new Date(viewDate.getFullYear() + 1, 0, 1))}
                    disabled={viewDate.getFullYear() >= currentYear}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
              {/* Month Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 2 }}>
                {MONTHS.map((month, index) => (
                  <MonthButton
                    key={month}
                    selected={isMonthSelected(index)}
                    inRange={isMonthInRange(index)}
                    disabled={isMonthDisabled(index)}
                    onClick={() => handleMonthSelect(index)}
                  >
                    {month}
                  </MonthButton>
                ))}
              </Box>

              {/* Selection hint */}
              <Box sx={{ p: 2, backgroundColor: '#F0F9FF', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '12px', color: '#0369A1' }}>
                  {selectedMonth && !endMonth 
                    ? `✓ ${FULL_MONTHS[selectedMonth.month]} ${selectedMonth.year} selected. Click another month to select a range.`
                    : selectedMonth && endMonth
                    ? `📅 ${FULL_MONTHS[selectedMonth.month]} ${selectedMonth.year} → ${FULL_MONTHS[endMonth.month]} ${endMonth.year}`
                    : '💡 Click a month to select. Click two months for a range.'}
                </Typography>
              </Box>
            </>
          )}

          {/* Year View */}
          {activeTab === 'year' && (
            <>
              <SectionTitle>Select Year</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
                {generateYears().map((year) => (
                  <YearButton
                    key={year}
                    selected={isYearSelected(year)}
                    inRange={isYearInRange(year)}
                    onClick={() => handleYearSelect(year)}
                  >
                    {year}
                  </YearButton>
                ))}
              </Box>

              {/* Selection hint */}
              <Box sx={{ p: 2, backgroundColor: '#F0F9FF', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '12px', color: '#0369A1' }}>
                  {selectedYear && !endYear 
                    ? `✓ ${selectedYear} selected. Click another year to select a range.`
                    : selectedYear && endYear
                    ? `📅 ${selectedYear} → ${endYear}`
                    : '💡 Click a year to select. Click two years for a range.'}
                </Typography>
              </Box>
            </>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
};

export default DateRangePicker;
